import { useEffect, useState } from 'react';
import type { RepositoryFile } from '@trove/core';
import { isHeicFile } from './fileKinds';
import { heicPreviewJpeg } from './heicPreview';
import { useFileObjectUrl } from './useFileObjectUrl';

export function useImagePreviewUrl(
  file: Pick<RepositoryFile, 'path' | 'content' | 'mimeType'> | undefined,
) {
  const needsHeic = Boolean(file && isHeicFile(file));
  const nativeUrl = useFileObjectUrl(
    file && !needsHeic ? file : undefined,
    file?.mimeType || 'image/*',
  );
  const [heicUrl, setHeicUrl] = useState<string>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!file || !isHeicFile(file)) {
      setHeicUrl(undefined);
      setFailed(false);
      return;
    }

    let cancelled = false;
    let objectUrl: string | undefined;
    setFailed(false);
    setHeicUrl(undefined);

    heicPreviewJpeg(file)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setHeicUrl(objectUrl);
      })
      .catch(() => {
        if (cancelled) return;
        setFailed(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file?.content, file?.mimeType, file?.path]);

  if (!file) return { src: undefined, loading: false, failed: false };
  if (needsHeic) {
    return { src: heicUrl, loading: !heicUrl && !failed, failed };
  }
  return { src: nativeUrl, loading: false, failed: false };
}
